"use client"

import { useEffect, useMemo, useState } from 'react'
import { ArrowDownToLine, ArrowUpFromLine, Banknote, Loader2, Trash2, X } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useStore } from '@/lib/store'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import {
  BANK_CODES, CARD_PROVIDER_LABELS, CASH_REASONS, DRAWER_EXPENSE_CATEGORIES,
  type BankCode, type CashDirection, type CashMovementReason, type DrawerExpenseCategory, type Shift,
} from '@/lib/mock-data'
import { lastBank, movementNote } from '@/lib/drawer-float'
import { movementText } from '@/lib/drawer-text'
import { clockText, useLang, useT, type Key } from '@/lib/i18n'
import { BankMark } from '@/components/ui/bank-mark'

/**
 * Money into or out of the drawer during a shift — always with a reason (107).
 *
 * Until this, the owner taking ₾200 home or a courier paid ₾15 from the till
 * reached the close as an unexplained „სხვაობა". Now each one is a named line
 * on the close, on the Z-report and in the owner's shift history.
 *
 * The owner's decisions (2026-09-24): whoever is on the shift may write one,
 * with no PIN — a courier paid while the owner is away is real life, and a
 * named line is a stronger check than an unnamed shortage; and an expense paid
 * from the drawer is booked as an expense in the same step, so nobody types it
 * twice. The server (`record_cash_movement`) holds both rules; this screen
 * only asks the questions in order.
 */

interface Props {
  open: boolean
  onClose: () => void
  shift: Shift | null
  /** Which way the menu asked for. The toggle inside can still change it. */
  direction: CashDirection
}

/** The server's sanity ceiling — a barcode typed into the amount box. */
const MAX_AMOUNT = 100000

/** The expense page's own words, said the way a till says them: „სხვა" is
 *  already a REASON above, so the category reads „სხვა ხარჯი"; transport is
 *  mostly the courier. The category booked is the same either way. */
const CATEGORY_WORDS: Record<DrawerExpenseCategory, Key> = {
  salary: 'drawer.cat.salary',
  transport: 'drawer.cat.transport',
  utilities: 'drawer.cat.utilities',
  other: 'drawer.cat.other',
}

export function CashMovementDialog({ open, onClose, shift, direction: asked }: Props) {
  const t = useT()
  const lang = useLang()
  // 24-hour on purpose: a headless check printed „03:26 AM" for ka-GE.
  const clock = (iso: string) => clockText(lang, iso)
  const recordCashMovement = useStore((s) => s.recordCashMovement)
  const deleteCashMovement = useStore((s) => s.deleteCashMovement)
  const allMoves = useStore((s) => s.cashMovements)
  const isOwner = useStore((s) => s.userRole === 'owner')
  const me = useStore((s) => s.cachedUserId)

  const [direction, setDirection] = useState<CashDirection>(asked)
  const [amount, setAmount] = useState('')
  // Out has no default on purpose: „the owner took it" and „an expense" are
  // both common, and a wrong default is a wrong line on the Z-report.
  const [reason, setReason] = useState<CashMovementReason | null>(null)
  const [category, setCategory] = useState<DrawerExpenseCategory>('other')
  const [bank, setBank] = useState<BankCode | null>(null)   // 120
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setDirection(asked)
    setReason(asked === 'in' ? 'change' : null)
    setAmount('')
    setNote('')
    setCategory('other')
    setConfirmId(null)
    // the bank named last is offered first — a shop mostly uses one
    const st = useStore.getState()
    setBank(lastBank(st.shifts, st.cashMovements))
  }, [open, asked])

  const moves = useMemo(
    () => (shift ? allMoves.filter((m) => m.shift_id === shift.id) : []),
    [allMoves, shift],
  )

  if (!shift) return null

  const out = direction === 'out'
  const switchTo = (d: CashDirection) => {
    setDirection(d)
    setReason(d === 'in' ? 'change' : null)
  }

  // Lari and at most two tetri, as the server wants them (it refuses 12.345).
  const clean = amount.trim().replace(',', '.')
  const value = Number(clean)
  const amountOk = /^\d{1,6}(\.\d{1,2})?$/.test(clean) && value > 0 && value <= MAX_AMOUNT
  const noteMissing = reason === 'other' && note.trim() === ''
  const bankMissing = reason === 'bank' && !bank   // 120 — a statement is matched by it
  const canSave = amountOk && reason !== null && !noteMissing && !bankMissing && !saving

  const save = async () => {
    if (!canSave || !reason) return
    setSaving(true)
    const ok = await recordCashMovement({
      direction,
      amount: value,
      reason,
      note,
      expenseCategory: reason === 'expense' ? category : null,
      bank: reason === 'bank' ? bank : null,
    })
    setSaving(false)
    if (!ok) return
    toast.success(t(out ? 'drawer.savedOut' : 'drawer.savedIn', { sum: `₾${value.toFixed(2)}` }))
    onClose()
  }

  const remove = async (id: string) => {
    setDeletingId(id)
    const ok = await deleteCashMovement(id)
    setDeletingId(null)
    setConfirmId(null)
    if (ok) toast.success(t('drawer.deleted'))
  }

  // The owner, or whoever wrote it — and only while the shift is open; never a
  // document's movement (a return, a receipt: it goes only with its document,
  // 170 MOVEMENT_LINKED). The server checks the same; this only hides a button
  // it would refuse.
  const canDelete = (m: { created_by: string | null; doc?: string | null }) =>
    !m.doc && shift.status === 'open' && (isOwner || (!!me && m.created_by === me))

  const totalIn = moves.filter((m) => m.direction === 'in').reduce((a, m) => a + m.amount, 0)
  const totalOut = moves.filter((m) => m.direction === 'out').reduce((a, m) => a + m.amount, 0)

  const label = 'text-[12px] font-bold text-muted-foreground uppercase tracking-wide'

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md rounded-[24px] p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-black flex items-center gap-2">
            {out
              ? <ArrowUpFromLine className="size-5 text-rose-600 dark:text-rose-400" />
              : <ArrowDownToLine className="size-5 text-emerald-600 dark:text-emerald-400" />}
            {out ? t('shift.cashOut') : t('shift.cashIn')}
          </DialogTitle>
          <DialogDescription>
            {t('drawer.desc', { name: shift.cashier_name ?? '—', time: clock(shift.opened_at) })}
          </DialogDescription>
        </DialogHeader>

        {/* which way */}
        <div className="grid grid-cols-2 gap-1 p-1 rounded-2xl bg-muted" role="tablist" aria-label={t('drawer.direction')}>
          {(['out', 'in'] as const).map((d) => (
            <button
              key={d}
              type="button"
              role="tab"
              aria-selected={direction === d}
              onClick={() => switchTo(d)}
              className={cn(
                'h-10 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 transition-colors',
                direction === d ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {d === 'out' ? <ArrowUpFromLine className="size-4" /> : <ArrowDownToLine className="size-4" />}
              {d === 'out' ? t('drawer.out') : t('drawer.in')}
            </button>
          ))}
        </div>

        {/* how much */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="cash-move-amount" className={label}>{t('drawer.amount')}</label>
          <div className="relative">
            <Banknote className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              id="cash-move-amount"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              autoFocus
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save() }}
              className="h-12 pl-9 rounded-xl text-lg font-bold tabular-nums bg-card dark:bg-white/5"
            />
          </div>
          {amount.trim() !== '' && !amountOk && (
            <p className="text-[12px] text-amber-700 dark:text-amber-400">
              {t('drawer.amountHint')}
            </p>
          )}
        </div>

        {/* why */}
        <div className="flex flex-col gap-1.5">
          <span className={label}>{t('drawer.reason')}</span>
          <div className="grid grid-cols-2 gap-2">
            {CASH_REASONS[direction].map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={reason === r}
                onClick={() => setReason(r)}
                className={cn(
                  'min-h-11 px-3 rounded-xl border text-sm font-semibold text-left transition-colors',
                  reason === r
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-card dark:bg-white/5 text-foreground hover:bg-muted',
                )}
              >
                {t(`reason.${r}` as const)}
              </button>
            ))}
          </div>
        </div>

        {/* 120 — a deposit says which bank: the statement is matched by it */}
        {reason === 'bank' && (
          <div className="flex flex-col gap-1.5">
            <span className={label}>{t('bank.which')}</span>
            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label={t('bank.which')}>
              {BANK_CODES.map((b) => (
                <button
                  key={b}
                  type="button"
                  role="radio"
                  aria-checked={bank === b}
                  onClick={() => setBank(b)}
                  className={cn(
                    'h-10 rounded-xl border text-sm font-bold transition-colors',
                    bank === b
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card dark:bg-white/5 text-foreground hover:bg-muted',
                  )}
                >
                  <span className="inline-flex items-center justify-center gap-1.5"><BankMark bank={b} className="size-4" />{CARD_PROVIDER_LABELS[b]}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* an expense says which kind — it is booked in ხარჯები too */}
        {reason === 'expense' && (
          <div className="flex flex-col gap-1.5">
            <span className={label}>{t('drawer.expenseKind')}</span>
            <div className="flex flex-wrap gap-1.5">
              {DRAWER_EXPENSE_CATEGORIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-pressed={category === c}
                  onClick={() => setCategory(c)}
                  className={cn(
                    'h-9 px-3 rounded-full border text-[13px] font-semibold transition-colors',
                    category === c
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-foreground/80 hover:bg-muted',
                  )}
                >
                  {t(CATEGORY_WORDS[c])}
                </button>
              ))}
            </div>
            <p className="text-[12px] text-muted-foreground leading-snug">
              {t('drawer.expenseNote')}
            </p>
          </div>
        )}

        <Input
          type="text"
          maxLength={200}
          placeholder={reason === 'other' ? t('drawer.notePhRequired') : out ? t('drawer.notePhOut') : t('drawer.notePhIn')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save() }}
          className="h-11 rounded-xl bg-card dark:bg-white/5"
        />

        <Button
          onClick={save}
          disabled={!canSave}
          className="h-12 rounded-xl font-black text-[15px] bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {saving
            ? <Loader2 className="size-4 animate-spin" />
            : <>{out ? t('drawer.out') : t('drawer.in')}{amountOk ? ` ₾${value.toFixed(2)}` : ''}</>}
        </Button>

        {/* this shift so far — and the way to take a wrong line back */}
        {moves.length > 0 && (
          <div className="flex flex-col gap-2 pt-1">
            <div className="flex items-center justify-between">
              <span className={label}>{t('drawer.thisShift')}</span>
              <span className="text-[12px] font-bold tabular-nums">
                {totalIn > 0 && <span className="text-emerald-600 dark:text-emerald-400">+₾{totalIn.toFixed(2)}</span>}
                {totalIn > 0 && totalOut > 0 && <span className="text-muted-foreground"> · </span>}
                {totalOut > 0 && <span className="text-rose-600 dark:text-rose-400">−₾{totalOut.toFixed(2)}</span>}
              </span>
            </div>
            <ul className="rounded-2xl border border-border divide-y divide-border overflow-hidden">
              {moves.map((m) => (
                <li key={m.id} className="flex items-center gap-2.5 px-3 py-2.5">
                  <span className={cn(
                    'size-8 rounded-lg flex items-center justify-center shrink-0',
                    m.direction === 'in'
                      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                      : 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
                  )}>
                    {m.direction === 'in' ? <ArrowDownToLine className="size-4" /> : <ArrowUpFromLine className="size-4" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-foreground truncate">
                      {movementText(t, m)}{movementNote(m) ? ` · ${movementNote(m)}` : ''}
                    </span>
                    <span className="block text-[12px] text-muted-foreground">{clock(m.created_at)}</span>
                  </span>
                  <span className={cn(
                    'text-sm font-black tabular-nums shrink-0',
                    m.direction === 'in' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400',
                  )}>
                    {m.direction === 'in' ? '+' : '−'}₾{m.amount.toFixed(2)}
                  </span>
                  {canDelete(m) && (confirmId === m.id ? (
                    <span className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => remove(m.id)}
                        disabled={deletingId === m.id}
                        className="h-8 px-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[12px] font-bold flex items-center"
                      >
                        {deletingId === m.id ? <Loader2 className="size-3.5 animate-spin" /> : t('common.delete')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmId(null)}
                        aria-label={t('drawer.keep')}
                        className="size-8 rounded-lg border border-border text-muted-foreground hover:text-foreground flex items-center justify-center"
                      >
                        <X className="size-3.5" />
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmId(m.id)}
                      aria-label={t('drawer.deleteAria')}
                      title={t('drawer.deleteTitle')}
                      className="size-8 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 flex items-center justify-center shrink-0"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  ))}
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
